/**
 * /facturas/:id — Vista de factura emitida (read-only).
 *
 * Muestra cabecera + líneas + totales + status DIAN. Si está en draft,
 * permite archivar (soft-delete con reason).
 *
 * El "Enviar a DIAN" se hace desde un worker async fuera de esta UI.
 * En un sprint futuro (G.7+) se agregará botón "Enviar ahora".
 */

import { useNavigate, useParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Download, FilePlus2, Loader2, Send, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { invoiceApi, type InvoiceStatus } from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { moneyFormatter } from '@/lib/money'

// Poll mientras la transmisión DIAN está en curso (mismo patrón que el
// cockpit de habilitación): refetch cada 10s hasta llegar a estado
// terminal (accepted/rejected/failed); apagado en el resto de estados.
const POLL_INTERVAL_MS = 10_000

const STATUS_BADGES: Record<InvoiceStatus, { bg: string; fg: string; label: string }> = {
  draft: { bg: 'bg-bg-elevated', fg: 'text-fg-secondary', label: 'Borrador' },
  submitting: { bg: 'bg-info/15', fg: 'text-info', label: 'Enviando a DIAN' },
  accepted: { bg: 'bg-success-tint', fg: 'text-success-fg', label: 'Aceptada DIAN' },
  rejected: { bg: 'bg-error-tint', fg: 'text-error-fg', label: 'Rechazada DIAN' },
  failed: { bg: 'bg-warning-tint', fg: 'text-warning-fg', label: 'Falló' },
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const nav = useNavigate()
  const qc = useQueryClient()

  const inv = useQuery({
    queryKey: ['invoice', activeOrgId, id],
    queryFn: () => invoiceApi.get(activeOrgId!, id!),
    enabled: !!activeOrgId && !!id,
    refetchInterval: (query) =>
      query.state.data?.status === 'submitting' ? POLL_INTERVAL_MS : false,
  })

  const archive = useMutation({
    mutationFn: (reason: string) => invoiceApi.archive(activeOrgId!, id!, reason),
    onSuccess: () => {
      toast.success('Factura archivada')
      nav(ROUTES.INVOICES)
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } }
      toast.error(`No se pudo archivar: ${e?.response?.data?.message ?? 'error'}`)
    },
  })

  // Disparar transmisión real a DIAN.
  const submit = useMutation({
    mutationFn: () => invoiceApi.submitToDIAN(activeOrgId!, id!),
    onSuccess: (res) => {
      if (res.async) {
        toast.message(res.message)
      } else if (res.accepted) {
        toast.success(res.message)
      } else {
        toast.error(res.message)
      }
      qc.invalidateQueries({ queryKey: ['invoice', activeOrgId, id] })
    },
    onError: (err: unknown) => {
      const e = err as { response?: { status?: number; data?: { error?: string; message?: string } } }
      const msg = e?.response?.data?.message ?? e?.response?.data?.error ?? 'error'
      if (e?.response?.status === 503) {
        toast.error(`DIAN no configurado: ${msg}`)
      } else {
        toast.error(`No se pudo enviar a DIAN: ${msg}`)
      }
    },
  })

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  if (!id) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Datos faltantes.
        </div>
      </AppShell>
    )
  }

  if (inv.isLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando factura...
        </div>
      </AppShell>
    )
  }

  if (inv.isError || !inv.data) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          {(inv.error as Error)?.message ?? 'No se pudo cargar la factura.'}
        </div>
      </AppShell>
    )
  }

  const d = inv.data
  const badge = STATUS_BADGES[d.status]
  // Formatea los montos en la moneda propia del documento (AT v1.9 FAR).
  const money = moneyFormatter(d.currency)

  // Descarga un artefacto legal (XML firmado / respuesta DIAN) como blob
  // y dispara la descarga en el navegador. El backend valida auth + org.
  const downloadDoc = async (kind: 'signed-xml' | 'application-response') => {
    try {
      const blob =
        kind === 'signed-xml'
          ? await invoiceApi.downloadSignedXML(activeOrgId!, id!)
          : await invoiceApi.downloadApplicationResponse(activeOrgId!, id!)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${d.doc_number}_${kind}.xml`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      const err = e as { response?: { status?: number } }
      if (err?.response?.status === 404) {
        toast.error('Ese documento aún no está disponible (¿ya se transmitió a DIAN?)')
      } else {
        toast.error('No se pudo descargar el documento')
      }
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <Link
          to={ROUTES.INVOICES}
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Volver al listado
        </Link>

        <header className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-mono">{d.doc_number}</h1>
            <p className="mt-1 text-sm text-fg-secondary">
              {d.doc_type === 'invoice'
                ? 'Factura electrónica'
                : d.doc_type === 'credit_note'
                ? 'Nota crédito'
                : 'Nota débito'}{' '}
              · Emisión {d.issue_date.slice(0, 10)} {d.issue_time}
            </p>
          </div>
          <span className={`rounded-full ${badge.bg} px-3 py-1 text-xs font-semibold ${badge.fg}`}>
            {badge.label}
          </span>
        </header>

        {d.cufe && (
          <div className="mt-4 rounded-md border border-border bg-bg-elevated px-3 py-2 text-xs">
            <span className="text-fg-muted">CUFE:</span>{' '}
            <span className="font-mono break-all">{d.cufe}</span>
          </div>
        )}

        {d.status_message && (
          <div className="mt-4 rounded-md border border-warning/30 bg-warning-tint p-3 text-xs text-warning-fg">
            <strong>Mensaje DIAN:</strong> {d.status_message}
          </div>
        )}

        <section className="mt-6 grid gap-4 md:grid-cols-2">
          <Card title="Adquirente">
            <Pair label="Razón social" value={d.customer_legal_name} />
            <Pair
              label="Identificación"
              value={`${d.customer_id_type}-${d.customer_id_number}${
                d.customer_verification_digit ? `-${d.customer_verification_digit}` : ''
              }`}
              mono
            />
            <Pair label="Dirección" value={d.customer_address} />
            <Pair
              label="Ciudad"
              value={`${d.customer_city}, ${d.customer_department} (${d.customer_country})`}
            />
            {d.customer_email && <Pair label="Email" value={d.customer_email} />}
            {d.customer_phone && <Pair label="Teléfono" value={d.customer_phone} />}
          </Card>

          <Card title="Pago + moneda">
            <Pair label="Forma de pago" value={d.payment_means} />
            <Pair label="Moneda" value={d.currency} />
            {d.currency !== 'COP' && d.exchange_rate && (
              <Pair label={`Tasa ${d.currency}→COP`} value={d.exchange_rate} />
            )}
            {d.due_date && <Pair label="Vence" value={d.due_date.slice(0, 10)} />}
          </Card>
        </section>

        {/* Anticipo pre-factura — solo si la factura lo trae (AT FBD) */}
        {d.prepaid_amount && (
          <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
              Anticipo recibido antes de la emisión
            </h2>
            <p className="mt-1 text-xs text-fg-muted">
              Anexo Técnico DIAN v1.9 FBD — serializado en{' '}
              <code>cac:PrepaidPayment</code> del UBL.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {d.prepaid_id && <Pair label="Identificador" value={d.prepaid_id} />}
              <Pair label="Monto" value={money.format(parseFloat(d.prepaid_amount))} />
              {d.prepaid_received_date && (
                <Pair
                  label="Fecha recibido"
                  value={d.prepaid_received_date.slice(0, 10)}
                />
              )}
              {d.prepaid_paid_date && (
                <Pair label="Fecha de pago" value={d.prepaid_paid_date.slice(0, 10)} />
              )}
            </div>
          </section>
        )}

        {/* Líneas */}
        <section className="mt-6 overflow-hidden rounded-xl border border-border bg-bg-surface">
          <h2 className="border-b border-border bg-bg-elevated px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
            Líneas
          </h2>
          <table className="w-full text-sm">
            <thead className="bg-bg-elevated text-left text-xs uppercase text-fg-muted">
              <tr>
                <th className="px-4 py-2">#</th>
                <th className="px-4 py-2">Descripción</th>
                <th className="px-4 py-2 text-right">Cant.</th>
                <th className="px-4 py-2 text-right">Precio</th>
                <th className="px-4 py-2 text-right">Subtotal</th>
                <th className="px-4 py-2 text-right">IVA</th>
                <th className="px-4 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {d.lines?.map((l) => (
                <tr key={l.id}>
                  <td className="px-4 py-2 font-mono text-xs">{l.line_number}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium">{l.description}</div>
                    {l.product_sku && (
                      <div className="text-xs text-fg-muted font-mono">SKU: {l.product_sku}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{l.quantity}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {money.format(parseFloat(l.unit_price))}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {money.format(parseFloat(l.line_subtotal))}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {l.tax_iva_excluded
                      ? 'Excl.'
                      : `${money.format(parseFloat(l.tax_iva_amount))} (${l.tax_iva_percent}%)`}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs font-semibold">
                    {money.format(parseFloat(l.line_total))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="mt-6 rounded-xl border-2 border-brand-500/40 bg-brand-50/30 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-700">
            Totales
          </h2>
          <dl className="mt-3 grid gap-2 text-sm">
            <Pair label="Subtotal" value={money.format(parseFloat(d.subtotal))} mono />
            {parseFloat(d.total_discount) > 0 && (
              <Pair label="Descuento total" value={money.format(parseFloat(d.total_discount))} mono />
            )}
            <Pair label="IVA total" value={money.format(parseFloat(d.tax_iva_total))} mono />
            {parseFloat(d.tax_inc_total) > 0 && (
              <Pair label="INC total" value={money.format(parseFloat(d.tax_inc_total))} mono />
            )}
            <div className="my-1 border-t border-brand-500/30" />
            <div className="flex items-center justify-between">
              <dt className="font-semibold">GRAN TOTAL</dt>
              <dd className="text-lg font-bold text-brand-700 font-mono">
                {money.format(parseFloat(d.grand_total))}
              </dd>
            </div>
          </dl>
        </section>

        {d.notes && (
          <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
              Observaciones
            </h2>
            <p className="mt-2 text-sm text-fg-secondary">{d.notes}</p>
          </section>
        )}

        {/* Descargas de los documentos legales: XML firmado + respuesta
            DIAN. Visibles cuando el documento ya pasó por la firma (CUFE
            presente). El endpoint devuelve 404 si el artefacto aún no
            existe (p.ej. la respuesta DIAN antes de transmitir). */}
        {d.cufe && (
          <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
              Descargas
            </h2>
            <p className="mt-1 text-xs text-fg-muted">
              XML firmado (XAdES) tal como se transmitió a DIAN, y la
              respuesta de DIAN cuando ya respondió.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => downloadDoc('signed-xml')}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-app px-4 py-2 text-sm font-medium text-fg-primary hover:bg-bg-elevated"
              >
                <Download className="h-4 w-4" /> XML firmado
              </button>
              <button
                type="button"
                onClick={() => downloadDoc('application-response')}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-app px-4 py-2 text-sm font-medium text-fg-primary hover:bg-bg-elevated"
              >
                <Download className="h-4 w-4" /> Respuesta DIAN
              </button>
            </div>
          </section>
        )}

        {/* Factura aceptada por DIAN → permitir crear NC/ND referenciándola.
            Deep-link a /facturas/nueva con doc_type + ref pre-cargados (el
            flujo de emisión de notas vive en InvoiceCreatePage). Solo aplica
            a facturas; una nota no se corrige con otra nota desde aquí. */}
        {d.status === 'accepted' && d.doc_type === 'invoice' && (
          <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
              Corregir o ajustar esta factura
            </h2>
            <p className="mt-1 text-xs text-fg-muted">
              Emite una nota electrónica referenciando esta factura. La nota
              crédito disminuye el valor (devolución, anulación, descuento); la
              nota débito lo aumenta (intereses, ajustes).
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Link
                to={`${ROUTES.INVOICE_NEW}?doc_type=credit_note&ref=${d.id}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-app px-4 py-2 text-sm font-semibold text-fg-primary hover:bg-bg-elevated"
              >
                <FilePlus2 className="h-4 w-4" /> Crear nota crédito
              </Link>
              <Link
                to={`${ROUTES.INVOICE_NEW}?doc_type=debit_note&ref=${d.id}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-app px-4 py-2 text-sm font-semibold text-fg-primary hover:bg-bg-elevated"
              >
                <FilePlus2 className="h-4 w-4" /> Crear nota débito
              </Link>
            </div>
          </section>
        )}

        {(d.status === 'draft' || d.status === 'failed') && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => {
                const reason = prompt('¿Por qué archivas este draft?')
                if (reason) archive.mutate(reason)
              }}
              disabled={archive.isPending || submit.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-surface px-4 py-2 text-sm text-error-fg hover:bg-error-tint disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" /> Archivar
            </button>

            {/* Enviar a DIAN. Confirmación previa porque la operación
                es PRODUCTIVA (queda registro en DIAN incluso si
                rechazan). */}
            <button
              type="button"
              onClick={() => {
                const ok = confirm(
                  '¿Enviar esta factura a DIAN?\n\n' +
                    'La transmisión queda registrada en DIAN inmediatamente. ' +
                    'Si DIAN rechaza, la factura quedará en status "rejected" — habrá ' +
                    'que crear una nueva con los datos corregidos.',
                )
                if (ok) submit.mutate()
              }}
              disabled={submit.isPending || archive.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submit.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {d.status === 'failed' ? 'Reintentar envío a DIAN' : 'Enviar a DIAN'}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </AppShell>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-bg-surface p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{title}</h3>
      <dl className="mt-2 space-y-1.5 text-sm">{children}</dl>
    </div>
  )
}

function Pair({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-fg-muted">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : 'text-sm'}>{value}</dd>
    </div>
  )
}
